import React from 'react';

type UseTabGuardOptions<T extends string> = {
    activeTab: T;
    hasChanges: boolean;
    onSave: () => Promise<void>;
    onDiscard: () => void;
    onCommitTab: (tab: T) => void;
};

export const useTabGuard = <T extends string>(
    options: UseTabGuardOptions<T>
) => {
    const { activeTab, hasChanges, onSave, onDiscard, onCommitTab } = options;
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [pendingTab, setPendingTab] = React.useState<T | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);

    const handleTabChange = React.useCallback(
        (key: string) => {
            const nextTab = key as T;
            if (nextTab === activeTab) return;
            if (!hasChanges) {
                onCommitTab(nextTab);
                return;
            }
            setPendingTab(nextTab);
            setIsModalOpen(true);
        },
        [activeTab, hasChanges, onCommitTab]
    );

    const handleModalClose = React.useCallback(() => {
        setIsModalOpen(false);
        setPendingTab(null);
    }, []);

    const handleDiscard = React.useCallback(() => {
        onDiscard();
        if (pendingTab) {
            onCommitTab(pendingTab);
        }
        setPendingTab(null);
        setIsModalOpen(false);
    }, [onCommitTab, onDiscard, pendingTab]);

    const handleSave = React.useCallback(async () => {
        setIsSaving(true);
        try {
            await onSave();
            if (pendingTab) {
                onCommitTab(pendingTab);
            }
            setPendingTab(null);
            setIsModalOpen(false);
        } finally {
            setIsSaving(false);
        }
    }, [onCommitTab, onSave, pendingTab]);

    return {
        isModalOpen,
        isSaving,
        handleTabChange,
        handleModalClose,
        handleDiscard,
        handleSave,
    };
};
